import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { CheckoutEventNames, CheckoutEventsData, CheckoutEventsTotals, CountryCode, PaddleEventData } from '@paddle/paddle-js';
import { take } from 'rxjs/operators';
import { AddOn } from 'src/app/pages/new-billing/models/payment.model';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import { SetTransactionIdCacheOption } from 'src/app/pages/new-billing/store/actions';
import { NewBillingState } from 'src/app/pages/new-billing/store/state';
import { BillingDetailsApiData, PlanId } from 'src/app/pages/new-billing/types/api.types';
import { PaymentSummaryInfo } from 'src/app/pages/new-subscribe-plans/store/model';
import { BillingService, CacheOption, TransactionIdTypes } from 'src/app/services/billing/billing.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';
import { PaddleService } from 'src/app/services/paddle/service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { PartialCtx, StaticTrackingContext, TrackingContext } from 'src/app/services/tracking/context';
import { normalizePrice } from 'src/app/util/price-formatter.util';

type CheckoutAddOnRow = Pick<AddOn, 'name' | 'shortDescription'> & {
  totalPrice: number;
};

export type CheckoutDialogData = {
  currentPlan: ConcretePlan;

  /** Indicates whether normal checkout or checkout for updating card is used. By default, this is false */
  isUpdatingCard?: boolean;

  /** Sets the plan id that will be passed to the transaction id API. By default, the `planId` that
   * is used is the one from the {@link currentPlan} */
  planId?: PlanId;

  /** Indicates whether the checkout is for v2. By default, this is false */
  isV2?: boolean;

  /**
   * Sets the data for the payment summary left section. When this data is set, the checkout
   * won't display the billing details as usual but instead, a summary of failed charges will be
   * shown.
   */
  paymentFailureSummaryData?: PaymentSummaryInfo;

  /** Callback that will be executed after successful checkout. */
  onCheckoutSuccess?: (eventData: CheckoutEventsData, dialogRef: MatDialogRef<CheckoutComponent>) => Promise<void>;

  /** Alias for onCheckoutSuccess, used by some components */
  checkoutSuccessCallback?: (eventData: CheckoutEventsData, dialogRef: MatDialogRef<CheckoutComponent>) => Promise<void>;

  /** Indicates whether the overlay loader should be displayed. By default, this is true */
  withOverlayLoader?: boolean;

  /** Tracking context that will be used for any tracking props. */
  trackingCtx: PartialCtx;
  socialProof?: boolean;

  /**
   * When false, checkout v2 hides the trial heads-up strip; the close button stays in the default
   * top-right position. When true or omitted, behavior is unchanged. Default true.
   */
  showTrialHeadsUpBanner?: boolean;
};

export type CheckoutDialogClose = {
  billingDetails?: BillingDetailsApiData;
};

type TrackingEvents = Extract<
  CheckoutEventNames,
  'checkout.completed' | 'checkout.customer.updated' | 'checkout.payment.initiated' | 'checkout.payment.failed' | 'checkout.error'
>;

const eventsMap: Record<TrackingEvents, string> = {
  'checkout.completed': 'Checkout completed',
  'checkout.customer.updated': 'Clicked Continue',
  'checkout.payment.initiated': 'Clicked on Pay Now',
  'checkout.payment.failed': 'Checkout payment failed',
  'checkout.error': 'Checkout internal error',
};

@UntilDestroy()
@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
  providers: [
    {
      provide: TrackingContext,
      useFactory: (dialogData: CheckoutDialogData) => {
        return new StaticTrackingContext(dialogData.trackingCtx);
      },
      deps: [MAT_DIALOG_DATA],
    },
  ],
})
export class CheckoutComponent implements OnInit, OnDestroy {
  currentPlan: ConcretePlan;
  isPaddleUpdated = false;
  status: 'loading' | 'updating' | 'idle' | 'error' = 'loading';
  withOverlayLoader: boolean;
  totalsSummary: CheckoutEventsTotals;
  isUnsupportedCountry = false;
  transactionIdCacheOption: CacheOption;
  companyLogos = [
    { filename: 'capterra.png', alt: 'Capterra Logo', width: '83px', height: '40px' },
    { filename: 'getapp.png', alt: 'Getapp logo', width: '83px', height: '40px' },
    { filename: 'g2.png', alt: 'G2 Logo', width: '83px', height: '40px' },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: CheckoutDialogData,
    public paymentProvider: PaddleService,
    protected billingService: BillingService,
    protected dialogRef: MatDialogRef<CheckoutComponent>,
    protected segment: SegmentService,
    protected store: Store,
    protected changeDetectorRef: ChangeDetectorRef,
    protected growthBook: GrowthBookService,
    protected readonly localizedPricingService: LocalizedPricingService,
    protected readonly translate: TranslateService,
  ) {
    const { currentPlan, withOverlayLoader = true, isV2 = false } = dialogData;

    this.currentPlan = currentPlan.clone();
    const paymentRecurrence = currentPlan.paymentModel.paymentRecurrence || (currentPlan.planId?.includes('yearly') ? 'year' : 'month');

    this.currentPlan.switchPlanRecurrence(paymentRecurrence);

    this.withOverlayLoader = withOverlayLoader;
    this.transactionIdCacheOption = store.selectSnapshot(NewBillingState.transactionIdCacheOption);
  }

  async ngOnInit() {
    // Reset error message when component is initialized
    this.paymentProvider.eventCallback$
      .pipe(
        untilDestroyed(this),
      )
      .subscribe(event => {
        this.trackingHandlerEvent(event.name);
        this.handleCheckoutEvents(event);
      });

    this.checkoutOpenEvent();
    this.segment.trackInAppEvents('CC Popup Screen', { ...this.dialogData.trackingCtx });
  }

  ngOnDestroy(): void {}

  get trialCardMessage(): string {
    return !this.currentPlan.paymentModel.cardLast4 ? 'billing.trialCreditCharged' : 'billing.taxesInfoMessage';
  }

  async checkoutOpenEvent() {
    this.status = 'loading';

    const transactionType: TransactionIdTypes = this.dialogData.isUpdatingCard ? 'updatePaymentDetails' : 'checkout';
    const discountId = this.currentPlan.paymentModel.discount?.code;

    try {
      const homeCta = await this.segment.getExperimentDataByName('popup-signup-trigger');

      /**
       * The campaign discount codes.
       * These are used if no campaign discount feature flag is set.
       */
      const campaignDiscounts = 'TIMEDOCTORPARTNER10,TIMEDOCTORPARTNER20,TIMEDOCTORPARTNER50,TDDEELPROMO10';

      /**
       * Checks if the home CTA (call to action) is part of the campaign discounts.
       * Converts homeCta to uppercase and checks its presence in campaignDiscounts.
       */
      const isCampaignDiscount = typeof homeCta === 'string' && campaignDiscounts.includes(homeCta.toUpperCase());

      const transactionPayload = {
        type: transactionType,
        planId: this.dialogData.planId,
        discountId: isCampaignDiscount ? discountId : undefined,
        cache: this.transactionIdCacheOption,
      };
      const geoInfo = await this.segment.getGeoInfo();
      const paddleCountries: { code: CountryCode, name: string }[] = this.growthBook.getFeatureValue('paddle-supported-countries', []);
      const countryCode = geoInfo?.geo?.country as CountryCode;



      this.billingService.getTransactionId(transactionPayload).pipe(take(1)).subscribe({
        next: transactionId => {
          this.paymentProvider.renderInlineCheckout({ transactionId }, { transactionType });
          this.store.dispatch(new SetTransactionIdCacheOption('store'));
        },
        error: () => {
          this.status = 'error';
        },
      });
    } catch (error) {
      this.status = 'error';
    }
  }

  toggleChanges(event: MatSlideToggleChange) {
    this.status = 'updating';

    const paymentRecurrence = event.checked ? 'year' : 'month';
    this.currentPlan.switchPlanRecurrence(paymentRecurrence);

    // If the planId is different from the current plan, we need to revalidate the cache from the transactionId
    const cacheOption = this.dialogData.currentPlan.planId !== this.currentPlan.planId ? 'no-cache' : 'store';
    this.store.dispatch(new SetTransactionIdCacheOption(cacheOption));

    this.dialogData.planId = this.currentPlan.planId;
    this.paymentProvider.updatePlan(this.currentPlan);
  }

  breadcrumbClick() {
    if (this.isPaddleUpdated) {
      this.checkoutOpenEvent();
    }
    this.isPaddleUpdated = false;
  }

  trackingHandlerEvent(eventName: CheckoutEventNames) {
    const eventNameMapped = eventsMap[eventName];

    if (eventNameMapped) this.segment.trackInAppEvents(eventNameMapped, { ...this.dialogData.trackingCtx });
  }

  // Handle the outside click event when the modal is closed
  onOutsideClick(event: MouseEvent) {
    if (event.target['className'] === 'cdk-overlay-backdrop cdk-overlay-dark-backdrop') {
      this.segment.trackInAppEvents('Click Outside of the CC popup screen',
        { ...this.dialogData.trackingCtx });
    }
  }

  private handleCheckoutEvents(event: PaddleEventData) {
    if (!event.name) this.status = 'idle';

    switch (event.name) {
      case CheckoutEventNames.CHECKOUT_CUSTOMER_UPDATED:
        this.isPaddleUpdated = true;
        break;

      case CheckoutEventNames.CHECKOUT_COMPLETED:
        this.checkoutCompleted(event.data);
        break;

      case CheckoutEventNames.CHECKOUT_LOADED:
      case CheckoutEventNames.CHECKOUT_UPDATED:
        this.handleCheckoutUpdates(event.data);
        break;
      case CheckoutEventNames.CHECKOUT_ERROR:
        this.status = 'error';
        break;
    }
  }

  private handleCheckoutUpdates(data: CheckoutEventsData): void {
    this.status = 'idle';
    this.totalsSummary = data.recurring_totals;
    this.changeDetectorRef.detectChanges();
  }

  private async checkoutCompleted(eventData: CheckoutEventsData) {
    if (this.dialogData.onCheckoutSuccess) {
      this.dialogData.onCheckoutSuccess(eventData, this.dialogRef);
    } else if (this.dialogData.checkoutSuccessCallback) {
      this.dialogData.checkoutSuccessCallback(eventData, this.dialogRef);
    }
  }

  private switchPlanRecurrenceDirectly(planId: PlanId): void {
    this.status = 'updating';
    this.currentPlan.switchPlanRecurrence(planId?.includes('yearly') ? 'year' : 'month');

    this.store.dispatch(new SetTransactionIdCacheOption(planId !== this.currentPlan.planId ? 'no-cache' : 'store'));

    this.dialogData.planId = planId;
    this.paymentProvider.updatePlan(this.currentPlan);
  }

  /**
   * Formats a price with localized currency for template usage
   * @param price - The price to format
   * @returns string - Formatted price with appropriate currency symbol
   */
  formatLocalizedPrice(price: number): string {
    const normalizedPrice = normalizePrice(price);
    const billingCurrency = this.currentPlan?.billingApiData?.billingDetails?.currency;
    const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();

    // Priority 1: Always respect billing currency if available
    if (billingCurrency && this.currentPlan) {
      return this.localizedPricingService.formatPriceWithCurrency(normalizedPrice, billingCurrency, 'excludePlanAndAddon');
    }

    // Priority 2: If no card is added, try to get price from price overrides based on user's country
    const noCardPrice = this.localizedPricingService.getLocalizedPriceForNoCard(this.currentPlan, normalizedPrice);
    if (noCardPrice) {
      return this.localizedPricingService.formatPriceWithCurrency(noCardPrice.price, noCardPrice.currency, 'excludePlanAndAddon');
    }

    // Priority 3: Use geo-based pricing if localized pricing is enabled and no billing currency
    if (isEnabled && !billingCurrency && this.currentPlan) {
      return this.localizedPricingService.formatLocalizedPriceForComponent(normalizedPrice, this.currentPlan, true, 'excludePlanAndAddon');
    }

    // Priority 4: Fallback to standard formatting
    return this.localizedPricingService.formatLocalizedPriceForComponent(normalizedPrice, this.currentPlan, true, 'excludePlanAndAddon');
  }

  /**
   * Gets the discount amount for display
   * @returns number - The discount amount
   */
  getDiscountAmount(): number {
    // Discount information
    return (this.totalsSummary?.discount ||
      (this.currentPlan.paymentModel.discount.type === 'percentage' || this.currentPlan.paymentModel.discount.type === 'amount_per_seat'
        ? this.currentPlan.paymentModel.discount.value * this.currentPlan.paymentModel.billableUsers
        : this.currentPlan.paymentModel.discount.value)) * -1;
  }

  get addons(): CheckoutAddOnRow[] {
    return (this.currentPlan?.paymentModel?.addOns ?? []).map(addon => ({
      name: addon.name,
      shortDescription: this.getAddonRateLabel(addon),
      totalPrice: this.getAddonTotalPrice(addon),
    }));
  }

  get planPriceDescription(): string {
    const pricePerUser = this.formatLocalizedPrice(this.currentPlan?.paymentModel?.priceMonthly);
    const userLabel = this.translate.instant('common.user').toLowerCase();
    const periodLabel = this.translate.instant('billing.shortMonth').toLowerCase();

    return `${pricePerUser}/${userLabel}/${periodLabel}`;
  }

  get totalPlanCostWithoutAddonsAndDiscounts(): number {
    return normalizePrice(this.currentPlan?.paymentModel?.totalBillingPrice || this.currentPlan?.unitPrice);
  }

  get taxAmount(): number {
    return this.totalsSummary?.tax || this.currentPlan?.paymentModel?.tax || 0;
  }

  get annualBillingDiscount(): number {
    return this.currentPlan?.paymentModel?.annualPayDiscount || 0;
  }

  get normalizedPromotionalDiscount(): number {
    if (this.totalsSummary?.discount) return this.totalsSummary.discount;

    const discount = this.currentPlan?.paymentModel?.discount;
    if (!discount) return 0;

    if (discount.type === 'percentage' || discount.type === 'amount_per_seat') {
      return (discount.value ?? 0) * (this.currentPlan.paymentModel.billableUsers ?? 0);
    }
    return (discount.value ?? 0);
  }

  get promotionalDiscountBadge(): string {
    const discount = this.currentPlan?.paymentModel?.discount;
    if (discount?.type !== 'percentage' || !discount.percentage) {
      return '';
    }

    return this.translate.instant('billing.percentOff', { value: discount.percentage });
  }

  get hasOtherItems(): boolean {
    return this.taxAmount > 0 || !!this.normalizedPromotionalDiscount || !!this.annualBillingDiscount;
  }

  get totalBillLabelKey(): string {
    switch (this.currentPlan?.paymentModel?.paymentRecurrence) {
      case 'year':
        return 'billing.totalPerYear';
      case 'semi_year':
        return 'billing.totalPer6Months';
      case 'quarter':
        return 'billing.totalPer3Months';
      default:
        return 'billing.totalPerMonth';
    }
  }

  get totalBillAmount(): number {
    return this.totalsSummary?.total || this.currentPlan?.paymentModel?.nextPaymentAmount || 0;
  }

  public removeSpaces(text: string): string {
    return text.replace(/\s+/g, '');
  }

  private getAddonRateLabel(addon: AddOn): string {
    const price = this.formatLocalizedPrice(addon.unitPrice || 0);
    const unit = addon.isPerAccount
      ? this.translate.instant('billing.account')
      : this.translate.instant('common.user');
    const period = addon.billingPeriod === 'yearly'
      ? this.translate.instant('billing.shortYear')
      : this.translate.instant('billing.shortMonth');

    return `${price}/${unit.toLowerCase()}/${period.toLowerCase()}`;
  }

  private getAddonTotalPrice(addon: AddOn): number {
    const unitPrice = addon.unitPrice || 0;
    const quantity = addon.isPerAccount
      ? 1
      : (addon.quantity || this.currentPlan?.paymentModel?.billableUsers || 0);

    if (unitPrice && !addon.isPerAccount) {
      const basePrice = unitPrice * quantity;
      return this.currentPlan?.paymentModel?.paymentRecurrence === 'year' && addon.billingPeriod === 'monthly'
        ? basePrice * 12
        : basePrice;
    }

    return unitPrice;
  }
}
